import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Loader2,
  Sparkles,
  Copy as CopyIcon,
  Check,
  RefreshCcw,
  AlertTriangle,
  Pencil,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useIsMobile } from '../../hooks/useMediaQuery';
import API_BASE_URL from '../../config/api';

/**
 * SocialPostGenerator
 *
 * The form + result UI for generating a single Facebook or Instagram post.
 * Visibility is controlled by the parent (SocialPostDialog).
 *
 * State is intentionally session-only: when the dialog closes and reopens, the
 * generator is a fresh instance (the dialog wrapper unmounts the body when
 * closed) and previous results are gone.
 */

const PLATFORMS = [
  { label: 'Instagram', value: 'instagram' },
  { label: 'Facebook', value: 'facebook' },
];

const CATEGORIES = [
  { label: 'Just listed', value: 'just_listed' },
  { label: 'Just sold', value: 'just_sold' },
  { label: 'Under contract', value: 'under_contract' },
  { label: 'Open house', value: 'open_house' },
  { label: 'Market update', value: 'market_update' },
  { label: 'Buyer / Seller tip', value: 'buyer_seller_tip' },
  { label: 'Personal branding', value: 'personal_branding' },
  { label: 'Community spotlight', value: 'community_spotlight' },
];

const MARKET_REQUIRED_CATEGORIES = new Set([
  'just_listed',
  'just_sold',
  'under_contract',
  'open_house',
  'market_update',
  'community_spotlight',
]);

const TONE_OVERRIDES = [
  { label: 'Use my default', value: '' },
  { label: 'Professional', value: 'professional' },
  { label: 'Friendly', value: 'friendly' },
  { label: 'Luxury', value: 'luxury' },
  { label: 'Educational', value: 'educational' },
  { label: 'Bold', value: 'bold' },
  { label: 'Personal', value: 'personal' },
];

const HUMOR_OVERRIDES = [
  { label: 'Use my default', value: '' },
  { label: 'Witty', value: 'witty' },
  { label: 'Light', value: 'light' },
  { label: 'Mostly professional', value: 'mostly_professional' },
  { label: 'None', value: 'none' },
];

const STATIC_DISCLAIMER =
  "Generated by AI. Review carefully before posting — you're responsible for the content and any disclosures your state requires.";

const findLabel = (options, value) => {
  const opt = options.find((o) => o.value === value);
  return opt ? opt.label : '';
};

const SocialPostGenerator = () => {
  const isMobile = useIsMobile();

  const [platform, setPlatform] = useState('instagram');
  const [category, setCategory] = useState('just_listed');
  const [market, setMarket] = useState('');
  const [toneOverride, setToneOverride] = useState('');
  const [humorOverride, setHumorOverride] = useState('');
  const [savedSetup, setSavedSetup] = useState(null); // null until first GET resolves

  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [previousPostTexts, setPreviousPostTexts] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [copyState, setCopyState] = useState('idle');

  // ----------------------------------------------------------------
  // Initial load — pull saved Branding answers
  // ----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/social/setup`, {
          withCredentials: true,
        });
        if (cancelled) return;
        const safe = {
          tone: data?.tone || '',
          humorLevel: data?.humorLevel || '',
          targetClients: Array.isArray(data?.targetClients) ? data.targetClients : [],
          primaryMarkets: Array.isArray(data?.primaryMarkets) ? data.primaryMarkets : [],
        };
        setSavedSetup(safe);
        if (safe.primaryMarkets.length > 0) {
          setMarket(safe.primaryMarkets[0]);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[SocialPostGenerator] could not load saved setup:', err?.response?.status);
          setSavedSetup({ tone: '', humorLevel: '', targetClients: [], primaryMarkets: [] });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const marketShown = useMemo(() => {
    if (!savedSetup) return false;
    return MARKET_REQUIRED_CATEGORIES.has(category) && savedSetup.primaryMarkets.length > 0;
  }, [savedSetup, category]);

  // Effective tone/humor labels for the collapsed summary pill
  const summaryToneLabel = toneOverride
    ? findLabel(TONE_OVERRIDES, toneOverride)
    : findLabel(TONE_OVERRIDES, savedSetup?.tone || '') || 'Default';
  const summaryHumorLabel = humorOverride
    ? findLabel(HUMOR_OVERRIDES, humorOverride)
    : findLabel(HUMOR_OVERRIDES, savedSetup?.humorLevel || '') || 'Default';

  // ----------------------------------------------------------------
  // Generate / Try Again
  // ----------------------------------------------------------------
  const runGenerate = async () => {
    setErrorMessage('');
    setIsGenerating(true);
    try {
      const body = {
        platform,
        category,
        excluded_post_texts: previousPostTexts.slice(-3),
        force: previousPostTexts.length > 0,
      };
      if (marketShown && market) body.market = market;
      if (toneOverride) body.tone_override = toneOverride;
      if (humorOverride) body.humor_override = humorOverride;

      const { data } = await axios.post(
        `${API_BASE_URL}/api/social/generate`,
        body,
        { withCredentials: true },
      );

      // Push the previous post text (if any) before assigning the new result
      setPreviousPostTexts((prev) => {
        if (result?.post_text) return [...prev, result.post_text];
        return prev;
      });
      setResult({
        post_text: String(data?.post_text || ''),
        hashtags: Array.isArray(data?.hashtags) ? data.hashtags.map((h) => String(h)) : [],
        image_suggestion: String(data?.image_suggestion || ''),
        disclaimer: String(data?.disclaimer || STATIC_DISCLAIMER),
      });
      setIsFormCollapsed(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErrorMessage('This feature requires a PRO plan.');
      } else if (status === 429) {
        setErrorMessage('Slow down — try again in a moment.');
      } else if (status === 400) {
        const detail = err?.response?.data?.detail;
        setErrorMessage(
          (typeof detail === 'string' && detail) ||
            "Something's off with the inputs — try different settings.",
        );
      } else {
        setErrorMessage("Couldn't generate right now. Try again in a few seconds.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // ----------------------------------------------------------------
  // Copy to clipboard
  // ----------------------------------------------------------------
  const handleCopy = async () => {
    if (!result) return;
    const text =
      result.post_text +
      (result.hashtags && result.hashtags.length ? `\n\n${result.hashtags.join(' ')}` : '');
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch (err) {
      console.warn('[SocialPostGenerator] clipboard write failed:', err?.message);
    }
  };

  // ----------------------------------------------------------------
  // Render — form (expanded) vs summary (collapsed)
  // ----------------------------------------------------------------
  const renderCollapsedSummary = () => {
    const pillClass =
      'inline-flex items-center rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 text-xs font-medium';
    return (
      <div
        className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3"
        data-testid="sp-summary-bar"
      >
        <span className={pillClass}>{findLabel(PLATFORMS, platform)}</span>
        <span className="text-gray-300">·</span>
        <span className={pillClass}>{findLabel(CATEGORIES, category)}</span>
        {marketShown && market && (
          <>
            <span className="text-gray-300">·</span>
            <span className={pillClass}>{market}</span>
          </>
        )}
        <span className="text-gray-300">·</span>
        <span className={pillClass}>
          {summaryToneLabel} · {summaryHumorLabel}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsFormCollapsed(false)}
          className="ml-auto h-8"
          data-testid="sp-edit-btn"
        >
          <Pencil className="w-3.5 h-3.5 mr-1" />
          Edit
        </Button>
      </div>
    );
  };

  // Wrap shadcn-Select onValueChange so the literal "__default__" maps back to ""
  // (shadcn Select rejects empty-string values). Apply to tone + humor.
  const wrappedSetTone = (v) => setToneOverride(v === '__default__' ? '' : v);
  const wrappedSetHumor = (v) => setHumorOverride(v === '__default__' ? '' : v);

  return (
    <div className="space-y-4" data-testid="social-post-generator">
      {isFormCollapsed ? renderCollapsedSummary() : renderExpandedFormBound()}

      {/* Errors live above the result card so they're highly visible */}
      {errorMessage && (
        <div
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2"
          role="alert"
          data-testid="sp-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Result card */}
      {result && (
        <div
          className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
          data-testid="sp-result-card"
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-primary text-white px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase">
              {findLabel(PLATFORMS, platform)}
            </span>
          </div>
          <p
            className="whitespace-pre-wrap text-[14px] leading-[1.55] text-[#092825]"
            data-testid="sp-post-text"
          >
            {result.post_text}
          </p>
          {result.hashtags && result.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="sp-hashtags">
              {result.hashtags.map((h, idx) => (
                <span
                  key={`${h}-${idx}`}
                  className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-0.5 text-[12px] font-medium"
                >
                  {h}
                </span>
              ))}
            </div>
          )}
          {result.image_suggestion && (
            <div
              className="text-[13px] text-gray-700 border-l-2 border-primary pl-3 py-1"
              data-testid="sp-image-suggestion"
            >
              <span className="font-semibold text-gray-900">Image idea:</span>{' '}
              {result.image_suggestion}
            </div>
          )}
        </div>
      )}

      {/* Action row + disclaimer (only when we have a result) */}
      {result && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              onClick={handleCopy}
              className="bg-primary hover:bg-primary/90 text-white"
              disabled={isGenerating}
              data-testid="sp-copy-btn"
            >
              {copyState === 'copied' ? (
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Copied
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CopyIcon className="w-4 h-4" />
                  Copy
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={runGenerate}
              disabled={isGenerating}
              data-testid="sp-try-again-btn"
            >
              {isGenerating ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <RefreshCcw className="w-4 h-4" />
                  Try again
                </span>
              )}
            </Button>
          </div>
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 leading-snug"
            data-testid="sp-disclaimer"
          >
            <strong>Generated by AI.</strong>{' '}
            {result.disclaimer
              ? result.disclaimer.replace(/^Generated by AI\.\s*/i, '')
              : STATIC_DISCLAIMER.replace(/^Generated by AI\.\s*/i, '')}
          </div>
        </>
      )}
    </div>
  );

  // Bridge — renderExpandedForm above uses the raw setters, but tone/humor need
  // the "__default__" sentinel mapping. We bind here.
  function renderExpandedFormBound() {
    return (
      <SocialPostFormBody
        platform={platform}
        setPlatform={setPlatform}
        category={category}
        setCategory={setCategory}
        marketShown={marketShown}
        market={market}
        setMarket={setMarket}
        savedSetup={savedSetup}
        toneOverride={toneOverride}
        setToneOverride={wrappedSetTone}
        humorOverride={humorOverride}
        setHumorOverride={wrappedSetHumor}
        isMobile={isMobile}
        isGenerating={isGenerating}
        onGenerate={runGenerate}
      />
    );
  }
};

/**
 * Inner form body. Extracted so the "__default__" sentinel-mapping setters
 * can be passed in cleanly without re-implementing the layout.
 */
const SocialPostFormBody = ({
  platform, setPlatform,
  category, setCategory,
  marketShown, market, setMarket,
  savedSetup,
  toneOverride, setToneOverride,
  humorOverride, setHumorOverride,
  isMobile, isGenerating, onGenerate,
}) => {
  const platformSelect = (
    <div>
      <Label className="text-xs uppercase tracking-wide text-gray-500">Platform</Label>
      <Select value={platform} onValueChange={setPlatform}>
        <SelectTrigger className="mt-1" data-testid="sp-platform">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PLATFORMS.map((p) => (
            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const categorySelect = (
    <div>
      <Label className="text-xs uppercase tracking-wide text-gray-500">Post type</Label>
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger className="mt-1" data-testid="sp-category">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CATEGORIES.map((c) => (
            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const marketSelect = marketShown ? (
    <div>
      <Label className="text-xs uppercase tracking-wide text-gray-500">Market</Label>
      <Select value={market} onValueChange={setMarket}>
        <SelectTrigger className="mt-1" data-testid="sp-market">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(savedSetup?.primaryMarkets || []).map((m) => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null;

  const toneValueForSelect = toneOverride === '' ? '__default__' : toneOverride;
  const humorValueForSelect = humorOverride === '' ? '__default__' : humorOverride;

  const toneSelect = (
    <div>
      <Label className="text-xs uppercase tracking-wide text-gray-500">Tone</Label>
      <Select value={toneValueForSelect} onValueChange={setToneOverride}>
        <SelectTrigger className="mt-1" data-testid="sp-tone-override">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TONE_OVERRIDES.map((t) => (
            <SelectItem key={t.value || 'default'} value={t.value || '__default__'}>
              {t.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const humorSelect = (
    <div>
      <Label className="text-xs uppercase tracking-wide text-gray-500">Humor</Label>
      <Select value={humorValueForSelect} onValueChange={setHumorOverride}>
        <SelectTrigger className="mt-1" data-testid="sp-humor-override">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {HUMOR_OVERRIDES.map((h) => (
            <SelectItem key={h.value || 'default'} value={h.value || '__default__'}>
              {h.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-3">
      {isMobile ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            {platformSelect}
            {categorySelect}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {toneSelect}
            {humorSelect}
          </div>
          {marketShown && marketSelect}
        </>
      ) : (
        <>
          <div className={`grid gap-3 ${marketShown ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {platformSelect}
            {categorySelect}
            {marketSelect}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {toneSelect}
            {humorSelect}
          </div>
        </>
      )}

      <Button
        type="button"
        onClick={onGenerate}
        disabled={isGenerating}
        className="w-full bg-primary hover:bg-primary/90 text-white"
        data-testid="sp-generate-btn"
      >
        {isGenerating ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating…
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Generate
          </span>
        )}
      </Button>
    </div>
  );
};

export default SocialPostGenerator;
